import {
  Component,
  AfterViewInit,
  ViewChild,
  ElementRef,
  Input,
  Output,
  EventEmitter,
  OnDestroy,
} from "@angular/core";
import SignaturePad from "signature_pad";

@Component({
  selector: "app-firma-canvas",
  templateUrl: "./firma-canvas.component.html",
  styleUrls: ["./firma-canvas.component.scss"],
})
export class FirmaCanvasComponent implements AfterViewInit, OnDestroy {
  @ViewChild("signatureCanvas") canvasRef: ElementRef<HTMLCanvasElement>;

  @Input() width: number = 300;
  @Input() height: number = 100;
  @Input() penColor: string = "#000000";

  @Output() firmaGenerada = new EventEmitter<File>();
  @Output() firmaBorrada = new EventEmitter<void>();

  private signaturePad: SignaturePad;
  isEmpty: boolean = true;

  ngAfterViewInit(): void {
    this.initSignaturePad();
  }

  ngOnDestroy(): void {
    if (this.signaturePad) {
      this.signaturePad.off();
    }
  }

  private initSignaturePad(): void {
    const canvas = this.canvasRef.nativeElement;

    // Ajustar para pantallas de alta densidad
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    canvas.width = this.width * ratio;
    canvas.height = this.height * ratio;
    canvas.style.width = `${this.width}px`;
    canvas.style.height = `${this.height}px`;
    canvas.getContext("2d").scale(ratio, ratio);

    this.signaturePad = new SignaturePad(canvas, {
      penColor: this.penColor,
      backgroundColor: "rgba(255, 255, 255, 0)",
    });

    this.signaturePad.addEventListener("beginStroke", () => {
      this.isEmpty = false;
    });
  }

  /**
   * Limpia el canvas
   */
  limpiar(): void {
    if (this.signaturePad) {
      this.signaturePad.clear();
      this.isEmpty = true;
      this.firmaBorrada.emit();
    }
  }

  /**
   * Confirma la firma y emite el archivo
   */
  confirmar(): void {
    if (this.signaturePad && !this.signaturePad.isEmpty()) {
      const file = this.canvasToFile();
      if (file) {
        this.firmaGenerada.emit(file);
      }
    }
  }

  /**
   * Convierte el canvas a un archivo PNG
   */
  private canvasToFile(): File | null {
    const dataUrl = this.signaturePad.toDataURL("image/png");
    const blob = this.dataUrlToBlob(dataUrl);

    if (!blob) {
      return null;
    }

    const timestamp = Date.now();
    return new File([blob], `firma_${timestamp}.png`, { type: "image/png" });
  }

  /**
   * Convierte data URL a Blob
   */
  private dataUrlToBlob(dataUrl: string): Blob | null {
    const parts = dataUrl.split(",");
    if (parts.length !== 2) {
      return null;
    }

    const mimeMatch = parts[0].match(/:(.*?);/);
    if (!mimeMatch) {
      return null;
    }

    const mime = mimeMatch[1];
    const bstr = atob(parts[1]);
    const n = bstr.length;
    const u8arr = new Uint8Array(n);

    for (let i = 0; i < n; i++) {
      u8arr[i] = bstr.charCodeAt(i);
    }

    return new Blob([u8arr], { type: mime });
  }

  /**
   * Devuelve el archivo de la firma si hay dibujo en el canvas
   */
  getFile(): File | null {
    if (this.signaturePad && !this.signaturePad.isEmpty()) {
      return this.canvasToFile();
    }
    return null;
  }

  /**
   * Verifica si el canvas tiene firma
   */
  get hasFirma(): boolean {
    return this.signaturePad && !this.signaturePad.isEmpty();
  }
}
